import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnChanges, type OnInit, Output, type SimpleChanges } from '@angular/core';
import {
    type FormArray,
    FormBuilder,
    type FormControl,
    type FormGroup,
    FormsModule,
    ReactiveFormsModule,
    type UntypedFormArray,
    Validators
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, firstValueFrom, type Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AdvizeoInputDirective } from '../../../directives/advizeo-input/advizeo-input.directive';
import type {
    BusinessIntelligenceDataSource,
    BusinessIntelligenceDataSourceCode,
    BusinessIntelligenceDimension,
    BusinessIntelligenceFilter,
    BusinessIntelligenceMeasure,
    BusinessIntelligenceMember,
    BusinessIntelligenceRepresentation
} from '../../../interfaces/BusinessIntelligence.interface';
import type { DashboardMode, DashboardScopeType } from '../../../interfaces/Dashboard.interface';
import {
    type BusinessIntelligenceDataSourceConfiguration,
    BusinessIntelligenceShellCode,
    type BusinessIntelligenceWidgetConfig,
    type BusinessIntelligenceWidgetConfiguration,
    TailoredWidgetType
} from '../../../interfaces/TailoredWidget.interface';
import { BusinessIntelligenceProvider } from '../../../providers/business-intelligence/business-intelligence.provider';
import { convertDimensionIntoMember, convertMeasureIntoMember } from '../../../providers/business-intelligence/business-intelligence.utils';
import { BusinessIntelligenceFilterEditorComponent } from '../../business-intelligence-filters/business-intelligence-filter-editor/business-intelligence-filter-editor';
import { PathListComponent } from '../../path-list/path-list';
import { PathListItemComponent } from '../../path-list/path-list-item/path-list-item';
import {
    BusinessIntelligenceWidgetRepresentationConfigEditorComponent,
    type RepresentationWidgetConfigEvent
} from '../business-intelligence-widget-representation-config-editor/business-intelligence-widget-representation-config-editor';
import type { BusinessIntelligenceWidgetConfigChangedEvent } from '../tailored-widget-config-editor/tailored-widget-config-editor';
import { BusinessIntelligenceRepresentationCode } from './../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { BusinessIntelligenceRepresentationsProvider } from './../../../providers/business-intelligence/business-intelligence-representations.provider';

export interface DisplayBusinessIntelligenceDimension extends BusinessIntelligenceDimension {
    resourceTypeLabel: string;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-widget-config-editor.scss'],
    selector: 'business-intelligence-widget-config-editor',
    templateUrl: 'business-intelligence-widget-config-editor.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UiSectionComponent,
        UiSectionContentComponent,
        UiFormFieldComponent,
        UiSelectComponent,
        UiFormErrorComponent,
        AdvizeoInputDirective,
        PathListComponent,
        PathListItemComponent,
        BusinessIntelligenceFilterEditorComponent,
        BusinessIntelligenceWidgetRepresentationConfigEditorComponent,
        AsyncPipe,
        TranslatePipe
    ]
})
export class BusinessIntelligenceWidgetConfigEditorComponent implements OnInit, OnChanges {
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _businessIntelligenceRepresentationsProvider = inject(BusinessIntelligenceRepresentationsProvider);
    private _formBuilder = inject(FormBuilder);
    private _referenceListsService = inject(ReferenceListsService);

    private _observableInputsManager = new ObservableInputsManager<BusinessIntelligenceWidgetConfigEditorComponent>(this);
    @Input() configToEdit!: BusinessIntelligenceWidgetConfig;
    configToEdit$ = this._observableInputsManager.observe('configToEdit');

    @Input() acceptedDashboardModes!: DashboardMode[];
    acceptedDashboardModes$ = this._observableInputsManager.observe('acceptedDashboardModes');

    @Input() dashboardScopeType!: DashboardScopeType;
    dashboardScopeType$ = this._observableInputsManager.observe('dashboardScopeType');

    @Output() configChanged: EventEmitter<BusinessIntelligenceWidgetConfigChangedEvent> =
        new EventEmitter<BusinessIntelligenceWidgetConfigChangedEvent>();

    @Input() isEditing!: boolean;

    formGroup!: FormGroup<{
        dataSource: FormControl<null | BusinessIntelligenceDataSourceCode>;
        representation: FormControl<BusinessIntelligenceRepresentationCode>;
        title: FormControl<string>;
        subtitle: FormControl<string>;
        filters: FormArray<FormControl<BusinessIntelligenceFilter>>;
    }>;
    businessIntelligenceDataSources$!: Observable<BusinessIntelligenceDataSource[]>;

    memberList$!: Observable<BusinessIntelligenceMember[]>;
    measureList$!: Observable<BusinessIntelligenceMeasure[]>;
    representableDimensionList$!: Observable<BusinessIntelligenceDimension[]>;

    representationWidgetConfig$!: Observable<BusinessIntelligenceRepresentation>;
    representationWidgetConfigEvent!: RepresentationWidgetConfigEvent;

    isConfigManuallyReset$ = new BehaviorSubject(false);
    resetIsNeeded$: Subject<void> = new Subject();

    ngOnInit() {
        this.businessIntelligenceDataSources$ = combineLatest([
            this.acceptedDashboardModes$.pipe(filter(isNotNullish)),
            this.dashboardScopeType$.pipe(filter(isNotNullish))
        ]).pipe(
            switchMap(([dashboardModes, dashboardScopeType]) =>
                this._businessIntelligenceProvider.getDashboardAvailableBusinessIntelligenceDataSources$(dashboardModes, dashboardScopeType)
            )
        );
        this.formGroup = this._formBuilder.nonNullable.group({
            dataSource: this._formBuilder.control<BusinessIntelligenceDataSourceCode | null>(null, Validators.required),
            representation: [BusinessIntelligenceRepresentationCode.HISTOGRAM, Validators.required],
            title: [''],
            subtitle: [''],
            filters: this._formBuilder.array<FormControl<BusinessIntelligenceFilter>>([])
        });

        observeControlValue$(this.formGroup.controls.dataSource)
            .pipe(
                filter(isNotNullish),
                switchMap(dataSourceCode => this._businessIntelligenceProvider.getBusinessIntelligenceDataSource$(dataSourceCode)),
                map(dataSource => dataSource.defaultFilters),
                shareReplay({ refCount: true, bufferSize: 1 }),
                untilDestroyed(this)
            )
            .subscribe(defaultFilters => {
                this.clearFilterList();
                defaultFilters.forEach(businessIntelligenceFilter =>
                    (this.formGroup.controls.filters as UntypedFormArray).push(this._formBuilder.control(businessIntelligenceFilter))
                );
            });

        this.representationWidgetConfig$ = this.configToEdit$.pipe(
            filter(configToEdit => isNotNullish(configToEdit) && isNotNullish(configToEdit.businessIntelligenceConfiguration)),
            map(configToEdit => {
                return configToEdit.businessIntelligenceConfiguration.representation;
            })
        );

        this.measureList$ = observeControlValue$(this.formGroup.controls.dataSource).pipe(
            startWith(
                this.configToEdit.businessIntelligenceConfiguration
                    ? this.configToEdit.businessIntelligenceConfiguration.dataSource.dataSourceCode
                    : null
            ),
            distinctUntilChanged(),
            filter(isNotNullish),
            switchMap(dataSourceCode => this._businessIntelligenceProvider.getBusinessIntelligenceDataSource$(dataSourceCode)),
            map(dataSource => dataSource.measures),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        const dimensionList$ = observeControlValue$(this.formGroup.controls.dataSource).pipe(
            startWith(
                this.configToEdit.businessIntelligenceConfiguration
                    ? this.configToEdit.businessIntelligenceConfiguration.dataSource.dataSourceCode
                    : null
            ),
            distinctUntilChanged(),
            filter(isNotNullish),
            switchMap(dataSourceCode => this._businessIntelligenceProvider.getBusinessIntelligenceDataSource$(dataSourceCode)),
            map(dataSource =>
                dataSource.dimensions.map(dimension => {
                    return {
                        ...dimension,
                        resourceTypeLabel: this._referenceListsService.getReferenceListItemLabelR(
                            ReferenceListName.RESOURCE_TYPE,
                            dimension.resourceTypeCode as string
                        )
                    };
                })
            ),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.representableDimensionList$ = dimensionList$.pipe(
            map(dimensions => dimensions.filter(dimension => !dimension.isFilterOnly)),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.memberList$ = combineLatest([
            this.measureList$.pipe(map(measures => measures.map(convertMeasureIntoMember))),
            dimensionList$.pipe(map(measures => measures.map(convertDimensionIntoMember)))
        ]).pipe(map(([measuresMembers, dimensionMembers]) => [...measuresMembers, ...dimensionMembers]));

        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.formValueChanged();
        });

        combineLatest([this.configToEdit$, this.measureList$, dimensionList$, this.isConfigManuallyReset$])
            .pipe(
                filter(
                    ([configToEdit, measureList, dimensionList, isConfigManuallyReset]) =>
                        isNotNullish(configToEdit) &&
                        isNotNullish(configToEdit.businessIntelligenceConfiguration) &&
                        measureList.length > 0 &&
                        dimensionList.length > 0 &&
                        !isConfigManuallyReset
                ),
                untilDestroyed(this)
            )
            .subscribe(([configToEdit, measures, dimensions, _]) => {
                const formConfig = this.getBusinessIntelligenceConfigFormFromWidgetConfiguration(
                    configToEdit.businessIntelligenceConfiguration
                );
                this.formGroup.patchValue(formConfig);
                this.formGroup.setControl('filters', this._formBuilder.nonNullable.array(formConfig.filters));
                this.representationWidgetConfigEvent = {
                    isValid: this._businessIntelligenceRepresentationsProvider.isRepresentationConfigValid(
                        configToEdit.businessIntelligenceConfiguration.representation.representationCode,
                        configToEdit.businessIntelligenceConfiguration.representation.representationConfig,
                        {
                            measures,
                            dimensions
                        }
                    ),
                    biRepresentation: configToEdit.businessIntelligenceConfiguration.representation
                };
                this.formValueChanged();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._observableInputsManager.triggerChanges(changes);
    }

    representationConfigChanged(config: RepresentationWidgetConfigEvent) {
        this.representationWidgetConfigEvent = config;
        this.formValueChanged();
    }

    async formValueChanged() {
        const formValue = this.formGroup.getRawValue();
        const representationValues: RepresentationWidgetConfigEvent = this.representationWidgetConfigEvent;
        const filters = formValue.filters.filter(isNotNullish);

        if (isNullish(formValue.dataSource)) {
            return;
        }

        const dataSourceConfiguration: BusinessIntelligenceDataSourceConfiguration = {
            dataSourceCode: formValue.dataSource,
            filters
        };

        const forbiddenDashboardModes = formValue.dataSource
            ? await firstValueFrom(
                  this._businessIntelligenceProvider.getBusinessIntelligenceDataSourceForbiddenDashboardModes$(formValue.dataSource)
              )
            : [];

        const config: BusinessIntelligenceWidgetConfigChangedEvent = {
            isValid: this.formGroup.valid && representationValues && representationValues.isValid,
            forbiddenDashboardModes,
            config: {
                widgetType: TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET,
                businessIntelligenceConfiguration: {
                    dataSource: dataSourceConfiguration,
                    representation: representationValues ? representationValues.biRepresentation : null!,
                    shell: {
                        shellCode: BusinessIntelligenceShellCode.CARD,
                        shellConfig: {
                            title: formValue.title,
                            subtitle: formValue.subtitle
                        }
                    }
                }
            }
        };
        this.configChanged.emit(config);
    }

    private getBusinessIntelligenceConfigFormFromWidgetConfiguration(
        widgetConfiguration: BusinessIntelligenceWidgetConfiguration
    ): ReturnType<typeof this.formGroup.getRawValue> {
        if (!widgetConfiguration || !widgetConfiguration.representation || !widgetConfiguration.dataSource) {
            return {
                dataSource: null,
                representation: BusinessIntelligenceRepresentationCode.HISTOGRAM,
                title: '',
                subtitle: '',
                filters: []
            };
        }

        return {
            dataSource: widgetConfiguration.dataSource.dataSourceCode,
            representation: widgetConfiguration.representation.representationCode,
            title:
                widgetConfiguration.shell && widgetConfiguration.shell.shellConfig && widgetConfiguration.shell.shellConfig.title
                    ? widgetConfiguration.shell.shellConfig.title.toString()
                    : '',
            subtitle:
                widgetConfiguration.shell && widgetConfiguration.shell.shellConfig && widgetConfiguration.shell.shellConfig.subtitle
                    ? widgetConfiguration.shell.shellConfig.subtitle.toString()
                    : '',
            filters: widgetConfiguration.dataSource && widgetConfiguration.dataSource.filters ? widgetConfiguration.dataSource.filters : []
        };
    }

    addFilter() {
        (this.formGroup.controls.filters as UntypedFormArray).push(this._formBuilder.control(null));
    }

    private clearFilterList() {
        while ((this.formGroup.controls.filters as UntypedFormArray).length !== 0) {
            (this.formGroup.controls.filters as UntypedFormArray).removeAt(0);
        }
    }

    onDataSourceChange() {
        this.isConfigManuallyReset$.next(true);
        this.resetIsNeeded$.next();
    }

    removeFilter(index: number) {
        (this.formGroup.controls.filters as UntypedFormArray).removeAt(index);
    }
}
